import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import categories from '@/mocks/categories';

const navLinks = [
  { to: '/', label: '首页', end: true },
  { to: '/fresh', label: '瓜田速递' },
  { to: '/about', label: '关于我们' },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname, location.search]);

  const goSubscribe = () => {
    setMobileOpen(false);
    const scroll = () => {
      const el = document.getElementById('subscribe');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    };
    if (location.pathname !== '/') {
      navigate('/');
      window.setTimeout(scroll, 80);
    } else {
      scroll();
    }
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-1 py-2 text-sm transition-colors cursor-pointer ${
      isActive
        ? 'text-primary-600 font-medium'
        : 'text-foreground-700 hover:text-primary-600'
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-background-50/80 backdrop-blur-sm border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 cursor-pointer shrink-0">
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-heart-3-fill text-lg" aria-hidden="true"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-label text-xl text-foreground-950">糖心吃瓜</span>
              <span className="text-[10px] tracking-widest text-primary-500 mt-0.5">
                SWEET GOSSIP
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7" aria-label="主导航">
            {navLinks.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                {item.label}
              </NavLink>
            ))}

            <div
              className="relative"
              onMouseEnter={() => setCatOpen(true)}
              onMouseLeave={() => setCatOpen(false)}
            >
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="flex items-center gap-1 py-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                aria-expanded={catOpen}
              >
                甜瓜分类
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    catOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                ></i>
              </button>
              {catOpen && (
                <div className="absolute right-0 top-full w-64 pt-2">
                  <div className="rounded-xl border border-background-200 bg-background-50 p-2">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/fresh?category=${cat.slug}`}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                      >
                        <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                          <i className={`${cat.icon} text-base`} aria-hidden="true"></i>
                        </span>
                        <span>{cat.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/fresh"
              className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line text-base" aria-hidden="true"></i>
              搜一搜甜瓜
            </Link>
            <button
              type="button"
              onClick={goSubscribe}
              className="rounded-full bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              订阅甜瓜日报
            </button>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="展开菜单"
            aria-expanded={mobileOpen}
          >
            <i className={mobileOpen ? 'ri-close-line text-2xl' : 'ri-menu-3-line text-2xl'} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-5 pt-3">
          <nav className="flex flex-col" aria-label="移动端导航">
            {navLinks.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `py-3 text-base border-b border-background-100 ${
                    isActive ? 'text-primary-600 font-medium' : 'text-foreground-800'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <p className="mt-4 mb-2 text-xs tracking-widest text-foreground-500">甜瓜分类</p>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/fresh?category=${cat.slug}`}
                className="flex items-center gap-2 rounded-lg bg-background-100 px-3 py-2.5 text-sm text-foreground-700 cursor-pointer"
              >
                <i className={`${cat.icon} text-primary-500`} aria-hidden="true"></i>
                {cat.name}
              </Link>
            ))}
          </div>
          <div className="mt-5 flex flex-col gap-3">
            <Link
              to="/fresh"
              className="rounded-full border border-background-300 bg-background-100 px-4 py-2.5 text-center text-sm text-foreground-700 cursor-pointer"
            >
              进入瓜田速递
            </Link>
            <button
              type="button"
              onClick={goSubscribe}
              className="rounded-full bg-primary-500 px-4 py-2.5 text-center text-sm text-background-50 cursor-pointer"
            >
              订阅甜瓜日报
            </button>
          </div>
        </div>
      )}
    </header>
  );
}